import { Text, View, XStack, YStack } from "tamagui";

import { colors } from "@styles/colors";
import { useValueTick } from "@screen/Home/pages/GasShop/skin";

/* 戴墨鏡比讚的那隻。用 -cool-hero 而不是原圖 gas-mascot-cool.png：
   原圖烘了一圈為白底畫的藍色擴散光（alpha ≤ 40 的像素占全圖 39%），
   壓在暖橘圓盤上會讀成一塊矩形髒污 —— 與 gas-mascot-hero 之於 -thumbsup 是同一件事。
   -cool-hero 另外裁掉了不對稱的透明邊（原圖角色偏左 6%、右邊留白給星星），
   所以這裡幾何置中就等於視覺置中，CSS 不需要任何補償位移。原圖保留未刪。 */
import MascotImg from "@styles/images/gas-mascot-cool-hero.png";

/** 對話框的邊線／尾巴同色，改一處兩處一起變（尾巴是旋轉 45° 的方塊，只留兩邊框線）。 */
const BUBBLE_BORDER = "#F7DFB5";

interface PointsSavingBannerProps {
  /** 折抵總額（元）。0 時整條不出現。 */
  discount: number;
}

/**
 * 「幫你省下 NT$ X」——由吉祥物說出口。
 *
 * 這是整個下單流程裡唯一一次告訴客戶「你少付了錢」的機會，做成一行小字會被滑過去
 *（優惠券那顆綠色膠囊就是為了同一個理由存在，見 CouponSwitchMenu 的 amountAccent）。
 *
 * 為什麼是對話而不是一條色帶：這個畫面上已經有三個東西在搶話——優惠券的綠膠囊、
 * 這條訊息、底部的藍色「確認送出 NT$ 530」。色帶要靠彩度才搶得到注意力，
 * 於是三者一起變響；角色說話靠的是「有人在跟你講話」，不必加重顏色就有存在感，
 * 也不會壓過底下那個客戶真正要確認的數字。
 *
 * 不寫「存氣 X 點 + 帳戶 Y 點」的拆帳：上面兩列各自已經寫明折了幾點、折掉多少錢。
 * 1 點折 1 元的情況下再寫一次，同一個數字就在一個畫面裡出現第四次。
 *
 * 動態都只跑一次（樣式在 all.scss 的 .points-save*，全在 prefers-reduced-motion 的關閉清單裡）：
 * 對話框從角色嘴邊彈出、角色身後擴散一圈暖光、金額用漸層字。
 * 金額之後再變動時彈一下（.gas-total-tick，與訂單摘要的應付金額同一個動作）——
 * 折抵是使用者剛剛撥動開關的結果，看不到變化就不確定生效了沒。
 */
const PointsSavingBanner = ({ discount }: PointsSavingBannerProps) => {
  const tick = useValueTick(discount);
  if (discount <= 0) return null;

  return (
    <XStack ai="flex-end" gap={8}>
      {/* 用原生 span 而不是 Tamagui 的 View：View 會帶自己的 atomic class（position 等），
          與 all.scss 的 .points-save-halo 同特異度，誰生效取決於注入順序 ——
          顯示與否不該交給注入順序決定。orderDone 的火花也是用 span，同一個理由。

          角色身下墊一顆暖色圓盤：這隻桶子本體是白的，而它站的地方不是白卡就是 #F5F7FB，
          縮到 54px 後整個人會融進背景（只剩眼睛和把手那點橘）。圓盤是為了讓它站得出來，
          不是裝飾——沒有它這個版本在手機上等於看不到角色。 */}
      <span
        style={{
          position: "relative",
          width: 54,
          height: 54,
          flex: "none",
          display: "block",
          borderRadius: 999,
          background: "radial-gradient(circle at 50% 45%, #FDECC8 0%, #FCE3AE 100%)",
        }}>
        {/* 圓盤外再擴散一圈暖光，只放一次 */}
        <span className="points-save-halo" aria-hidden />
        <img
          src={MascotImg}
          alt=""
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: 52,
            height: "auto",
            transform: "translate(-50%, -50%)",
            display: "block",
          }}
        />
      </span>

      <YStack
        className="points-save-say"
        f={1}
        miw={0}
        position="relative"
        bg={colors.light}
        bw={1.5}
        bc={BUBBLE_BORDER}
        br={14}
        px={14}
        py={10}
        style={{ boxShadow: "0 3px 12px -6px rgba(245,158,11,0.5)" }}>
        {/* 指向角色的尾巴：旋轉 45° 的方塊，只留左、下兩邊框線，疊在對話框左緣 */}
        <View
          position="absolute"
          l={-7}
          b={14}
          w={12}
          h={12}
          bg={colors.light}
          style={{
            borderLeft: `1.5px solid ${BUBBLE_BORDER}`,
            borderBottom: `1.5px solid ${BUBBLE_BORDER}`,
            transform: "rotate(45deg)",
          }}
        />
        <Text fontSize={12.5} fontWeight="700" color={colors.amberDark}>
          幫你省下
        </Text>
        <Text
          className={`points-save-amount ${tick ?? ""}`}
          fontSize={22}
          fontWeight="800"
          lineHeight={26}
          mt={2}
          style={{ fontVariantNumeric: "tabular-nums" }}>
          NT$ {discount.toLocaleString()}
        </Text>
      </YStack>
    </XStack>
  );
};

export default PointsSavingBanner;
